import { useCallback, useEffect, useMemo, useState } from "react";
import { message } from "antd";
import { getInstitutionManage, getInstitutionExceptAccounts } from "../../../../api/institutionApi";
import { getAccountsByInstitutionId } from "../../../../api/bankAccountApi";
import { hasRole } from "../../../../api/auth";
import {
  createInternalTransaction,
  createManualTransaction,
} from "../../../../api/bankReportStatementApi";
import {
  buildManualTHIncomeTransactionRequest,
  buildInternalTransactionRequest,
} from "../utils/institutionWalletUtils";
import { getApiErrorMessage } from "../../../../utils/apiErrorUtils";

const MANUAL_SENDER_INSTITUTION_ID = 14;

export function useInstitutionWallets(
  institutionId,
  navigate,
  initialCurrency = null
) {
  const [institution, setInstitution] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [manualSenderAccounts, setManualSenderAccounts] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [transactionModalOpen, setTransactionModalOpen] = useState(false);
  const [transactionMode, setTransactionMode] = useState("MANUAL");
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [transactionSubmitting, setTransactionSubmitting] = useState(false);

  const [externalInstitutions, setExternalInstitutions] = useState([]);
  const [externalInstitutionsLoading, setExternalInstitutionsLoading] = useState(false);

  const isAdmin = hasRole("ADMIN");

  const loadExternalInstitutions = async () => {
    if (!selectedAccount?.institutionId) return;

    try {
      setExternalInstitutionsLoading(true);
      const response = await getInstitutionExceptAccounts(selectedAccount.institutionId);
      setExternalInstitutions(response || []);
    } catch (error) {
      console.error("Failed to load external institutions", error);
      setExternalInstitutions([]);
    } finally {
      setExternalInstitutionsLoading(false);
    }
  };

  const handleTransferScopeChange = async (scope) => {
    if (scope === "EXTERNAL" && externalInstitutions.length === 0) {
      await loadExternalInstitutions();
    }
  };

  const loadData = useCallback(async () => {
    if (!institutionId) {
      setError("Institution id is missing");
      setInstitution(null);
      setCurrencies([]);
      setManualSenderAccounts([]);
      setSelectedCurrency(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const isAdmin = hasRole("ADMIN");

      const requests = [getInstitutionManage(institutionId)];

      if (isAdmin) {
        requests.push(getAccountsByInstitutionId(MANUAL_SENDER_INSTITUTION_ID));
      }

      const [manageResponse, senderAccountsResponse] = await Promise.all(requests);

      setInstitution(manageResponse?.institution || null);

      const currencyGroups = Array.isArray(manageResponse?.currencies)
        ? manageResponse.currencies
        : [];

      setCurrencies(currencyGroups);
      setManualSenderAccounts(
        isAdmin && Array.isArray(senderAccountsResponse) ? senderAccountsResponse : []
      );
    } catch (err) {
      console.error(err);
      setError(getApiErrorMessage(err) || "Failed to load institution wallets");
      setInstitution(null);
      setCurrencies([]);
      setManualSenderAccounts([]);
      setSelectedCurrency(null);
    } finally {
      setLoading(false);
    }
  }, [institutionId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const groupedByCurrency = useMemo(() => {
    return currencies.map((group) => {
      const accounts = Array.isArray(group.accounts) ? group.accounts : [];

      const walletBalance = accounts.reduce(
        (sum, account) => sum + Number(account?.walletBalance || 0),
        0
      );

      const totalBalance = accounts.reduce(
        (sum, account) => sum + Number(account?.balance || 0),
        0
      );

      return {
        currency: group.currencyCode,
        walletBalance,
        totalBalance,
        accounts,
      };
    });
  }, [currencies]);

  useEffect(() => {
    if (!groupedByCurrency.length) {
      setSelectedCurrency(null);
      return;
    }

    const initialExists = groupedByCurrency.some(
      (group) => group.currency === initialCurrency
    );

    if (initialExists) {
      setSelectedCurrency(initialCurrency);
      return;
    }

    setSelectedCurrency((prev) => {
      const prevExists = groupedByCurrency.some(
        (group) => group.currency === prev
      );

      return prevExists ? prev : groupedByCurrency[0]?.currency || null;
    });
  }, [groupedByCurrency, initialCurrency]);

  const selectedCurrencyGroup = useMemo(() => {
    return (
      groupedByCurrency.find((group) => group.currency === selectedCurrency) ||
      null
    );
  }, [groupedByCurrency, selectedCurrency]);

  const manualCurrencyMatchedSenderAccounts = useMemo(() => {
    if (transactionMode !== "MANUAL" || !selectedAccount?.currency) {
      return [];
    }

    return manualSenderAccounts.filter(
      (account) => account?.currency === selectedAccount.currency
    );
  }, [manualSenderAccounts, selectedAccount, transactionMode]);

const internalInstitutionAccounts = useMemo(() => {
  if (transactionMode !== "TRANSFER" || !selectedAccount?.id) {
    return [];
  }

  const allAccounts = groupedByCurrency.flatMap((group) =>
    Array.isArray(group.accounts) ? group.accounts : []
  );

  return allAccounts.filter(
    (account) =>
      account.id !== selectedAccount.id &&
      account.is_active === true
  );
}, [transactionMode, selectedAccount, groupedByCurrency]);

  const closeTransactionModal = useCallback(() => {
    setTransactionModalOpen(false);
    setSelectedAccount(null);
    setExternalInstitutions([]);
  }, []);

  const openManualModal = useCallback((account, event) => {
    event?.preventDefault();
    event?.stopPropagation();

    setSelectedAccount(account || null);
    setTransactionMode("MANUAL");
    setTransactionModalOpen(true);
  }, []);

  const openTransferModal = useCallback((account, event) => {
    event?.preventDefault();
    event?.stopPropagation();

    setSelectedAccount(account || null);
    setTransactionMode("TRANSFER");
    setTransactionModalOpen(true);
  }, []);

  const handleViewAccount = useCallback(
    (account, event) => {
      event?.preventDefault();
      event?.stopPropagation();

      if (!account?.id) return;
      navigate(`/account/${account.id}`);
    },
    [navigate]
  );

  const handleSubmitTransaction = useCallback(
    async (payload) => {
      try {
        setTransactionSubmitting(true);

        const request =
          transactionMode === "MANUAL"
            ? buildManualTHIncomeTransactionRequest(payload, selectedAccount)
            : buildInternalTransactionRequest(payload, selectedAccount);

        const response =
          transactionMode === "MANUAL"
            ? await createManualTransaction(request)
            : await createInternalTransaction(request);

        message.success(response?.message || "Transaction created successfully");
        closeTransactionModal();
        await loadData();
      } catch (err) {
        console.error(err);
        message.error(getApiErrorMessage(err));
        throw err;
      } finally {
        setTransactionSubmitting(false);
      }
    },
    [transactionMode, selectedAccount, closeTransactionModal, loadData]
  );

  return {
    institution,
    groupedByCurrency,
    selectedCurrency,
    selectedCurrencyGroup,
    loading,
    error,
    transactionModalOpen,
    transactionMode,
    selectedAccount,
    transactionSubmitting,
    manualCurrencyMatchedSenderAccounts,
    internalInstitutionAccounts,
    setSelectedCurrency,
    loadData,
    closeTransactionModal,
    openManualModal,
    openTransferModal,
    handleSubmitTransaction,
    handleViewAccount,
    manualSenderInstitutionId: MANUAL_SENDER_INSTITUTION_ID,
    externalInstitutions,
    externalInstitutionsLoading,
    handleTransferScopeChange,
  };
}